import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Music, Star, Sparkles, Play, Pause, RotateCcw, Check, Volume2, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { questsAPI } from '../api';

// ============ MUSIC NOTES DATA ============
const NOTES = {
  C4: { name: 'C', octave: 4, frequency: 261.63, staffPosition: 0, spokenName: 'This is Middle C' },
  D4: { name: 'D', octave: 4, frequency: 293.66, staffPosition: 1, spokenName: 'This is D' },
  E4: { name: 'E', octave: 4, frequency: 329.63, staffPosition: 2, spokenName: 'This is E' },
  F4: { name: 'F', octave: 4, frequency: 349.23, staffPosition: 3, spokenName: 'This is F' },
  G4: { name: 'G', octave: 4, frequency: 392.00, staffPosition: 4, spokenName: 'This is G' },
  A4: { name: 'A', octave: 4, frequency: 440.00, staffPosition: 5, spokenName: 'This is A' },
  B4: { name: 'B', octave: 4, frequency: 493.88, staffPosition: 6, spokenName: 'This is B' },
  C5: { name: 'C', octave: 5, frequency: 523.25, staffPosition: 7, spokenName: 'This is High C' },
};

const NOTE_ORDER = ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4', 'C5'];

// Simple melodies for Play Constellation
const MELODIES = [
  { name: 'Twinkle Start', notes: ['C4', 'C4', 'G4', 'G4', 'A4', 'A4', 'G4'], tempo: 100 },
  { name: 'Simple Scale', notes: ['C4', 'D4', 'E4', 'F4', 'G4'], tempo: 120 },
  { name: 'Happy Jump', notes: ['C4', 'E4', 'G4', 'C5'], tempo: 100 },
  { name: 'Echo Song', notes: ['G4', 'E4', 'G4', 'E4', 'G4'], tempo: 110 },
  { name: 'Star Path', notes: ['C4', 'D4', 'E4', 'D4', 'C4'], tempo: 100 },
];

// Rhythm patterns for Rhythm Builder
const RHYTHM_PATTERNS = [
  { name: 'Steady Beat', pattern: [1, 1, 1, 1], difficulty: 1 },
  { name: 'Skip Beat', pattern: [1, 0, 1, 0], difficulty: 1 },
  { name: 'Gallop', pattern: [1, 1, 0, 1], difficulty: 2 },
  { name: 'Syncopation', pattern: [0, 1, 1, 0], difficulty: 2 },
  { name: 'Triple Time', pattern: [1, 0, 0, 1, 0, 0], difficulty: 3 },
];

const MusicLearningGarden = ({ onBack, selectedChild, onAwardSparks }) => {
  // Activity state
  const [currentStage, setCurrentStage] = useState('menu'); // menu, staff-explorer, rhythm-builder, play-constellation, compose-star
  const [currentStep, setCurrentStep] = useState(0);
  
  // Staff Explorer state
  const [highlightedNote, setHighlightedNote] = useState(null);
  const [exploredNotes, setExploredNotes] = useState([]);
  
  // Rhythm Builder state
  const [currentRhythm, setCurrentRhythm] = useState(null);
  const [userRhythm, setUserRhythm] = useState([]);
  const [rhythmPlaying, setRhythmPlaying] = useState(false);
  const [rhythmScore, setRhythmScore] = useState(null);
  
  // Play Constellation state
  const [currentMelody, setCurrentMelody] = useState(null);
  const [melodyIndex, setMelodyIndex] = useState(0);
  const [userMelodyInput, setUserMelodyInput] = useState([]);
  const [melodyPlaying, setMelodyPlaying] = useState(false);
  const [melodyComplete, setMelodyComplete] = useState(false);
  
  // Compose Star Song state
  const [composition, setComposition] = useState([]);
  const [isPlayingComposition, setIsPlayingComposition] = useState(false);
  
  // Audio context
  const audioContextRef = useRef(null);
  
  // Initialize audio context
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);
  
  // Play a note
  const playNote = useCallback((noteKey, duration = 0.4) => {
    if (!audioContextRef.current) return;
    
    const note = NOTES[noteKey];
    if (!note) return;
    
    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(note.frequency, ctx.currentTime);
    
    // Add slight attack and release for smoother sound
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.05);
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  }, []);
  
  // Play a click/tap sound for rhythm
  const playTap = useCallback(() => {
    if (!audioContextRef.current) return;
    
    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(600, ctx.currentTime);
    
    gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.1);
  }, []);
  
  // Speak note name using Web Speech API
  const speakNoteName = useCallback((noteKey) => {
    const note = NOTES[noteKey];
    if (!note || !note.spokenName) return;
    
    // Check if speech synthesis is available
    if (!('speechSynthesis' in window)) return;
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    // Create utterance with friendly voice settings
    const utterance = new SpeechSynthesisUtterance(note.spokenName);
    utterance.rate = 1.0; // Normal speed
    utterance.pitch = 1.1; // Slightly higher pitch for friendliness
    utterance.volume = 0.8;
    
    // Try to get a friendly English voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => 
      v.lang.startsWith('en') && (v.name.includes('Female') || v.name.includes('Samantha') || v.name.includes('Google'))
    ) || voices.find(v => v.lang.startsWith('en'));
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    // Speak after a short delay to let the musical tone play first
    setTimeout(() => {
      window.speechSynthesis.speak(utterance);
    }, 300);
  }, []);
  
  // ============ STAFF EXPLORER FUNCTIONS ============
  const handleNoteClick = (noteKey) => {
    setHighlightedNote(noteKey);
    playNote(noteKey);
    
    // Speak the note name after the musical tone
    speakNoteName(noteKey);
    
    if (!exploredNotes.includes(noteKey)) {
      setExploredNotes(prev => [...prev, noteKey]);
    }
  };
  
  const completeStaffExplorer = async () => {
    toast.success('Staff Explorer Complete! +5 Sparks');
    if (onAwardSparks) {
      await onAwardSparks(5, 'discovery');
    }
    setCurrentStage('menu');
  };
  
  // ============ RHYTHM BUILDER FUNCTIONS ============
  const startRhythmBuilder = () => {
    const rhythm = RHYTHM_PATTERNS[Math.floor(Math.random() * 3)]; // Start with easier ones
    setCurrentRhythm(rhythm);
    setUserRhythm([]);
    setRhythmScore(null);
    setCurrentStage('rhythm-builder');
  };
  
  const playRhythmPattern = async () => {
    if (!currentRhythm || rhythmPlaying) return;
    
    setRhythmPlaying(true);
    const beatDuration = 500; // ms per beat
    
    for (let i = 0; i < currentRhythm.pattern.length; i++) {
      if (currentRhythm.pattern[i] === 1) {
        playTap();
      }
      await new Promise(resolve => setTimeout(resolve, beatDuration));
    }
    
    setRhythmPlaying(false);
  };
  
  const handleRhythmTap = () => {
    if (userRhythm.length >= currentRhythm.pattern.length) return;
    
    playTap();
    setUserRhythm(prev => [...prev, 1]);
  };
  
  const handleRhythmRest = () => {
    if (userRhythm.length >= currentRhythm.pattern.length) return;
    
    setUserRhythm(prev => [...prev, 0]);
  };
  
  const checkRhythm = async () => {
    const correct = userRhythm.join('') === currentRhythm.pattern.join('');
    setRhythmScore(correct);
    
    if (correct) {
      toast.success('Perfect rhythm! +5 Sparks');
      if (onAwardSparks) {
        await onAwardSparks(5, 'discovery');
      }
    }
  };
  
  const resetRhythm = () => {
    setUserRhythm([]);
    setRhythmScore(null);
  };
  
  // ============ PLAY CONSTELLATION FUNCTIONS ============
  const startPlayConstellation = () => {
    const melody = MELODIES[Math.floor(Math.random() * MELODIES.length)];
    setCurrentMelody(melody);
    setMelodyIndex(0);
    setUserMelodyInput([]);
    setMelodyComplete(false);
    setCurrentStage('play-constellation');
  };
  
  const playMelodyDemo = async () => {
    if (!currentMelody || melodyPlaying) return;
    
    setMelodyPlaying(true);
    const beatDuration = 60000 / currentMelody.tempo;
    
    for (const noteKey of currentMelody.notes) {
      playNote(noteKey, beatDuration / 1000);
      await new Promise(resolve => setTimeout(resolve, beatDuration));
    }
    
    setMelodyPlaying(false);
  };
  
  const handleMelodyNotePress = async (noteKey) => {
    if (!currentMelody || melodyComplete) return;
    
    playNote(noteKey);
    
    const expectedNote = currentMelody.notes[melodyIndex];
    const isCorrect = noteKey === expectedNote;
    
    setUserMelodyInput(prev => [...prev, { note: noteKey, correct: isCorrect }]);
    
    if (isCorrect) {
      if (melodyIndex + 1 >= currentMelody.notes.length) {
        setMelodyComplete(true);
        toast.success('Melody Complete! +10 Sparks');
        if (onAwardSparks) {
          await onAwardSparks(10, 'creativity');
        }
      } else {
        setMelodyIndex(prev => prev + 1);
      }
    } else {
      toast.error('Try again!');
      setMelodyIndex(0);
      setUserMelodyInput([]);
    }
  };
  
  // ============ COMPOSE STAR SONG FUNCTIONS ============
  const addNoteToComposition = (noteKey) => {
    if (composition.length >= 16) return;
    playNote(noteKey);
    setComposition(prev => [...prev, noteKey]);
  };
  
  const removeLastNote = () => {
    setComposition(prev => prev.slice(0, -1));
  };
  
  const clearComposition = () => {
    setComposition([]);
  };
  
  const playComposition = async () => {
    if (composition.length === 0 || isPlayingComposition) return;
    
    setIsPlayingComposition(true);
    
    for (const noteKey of composition) {
      playNote(noteKey, 0.3);
      await new Promise(resolve => setTimeout(resolve, 350));
    }
    
    setIsPlayingComposition(false);
  };
  
  const saveComposition = async () => {
    if (composition.length < 4) {
      toast.error('Add at least 4 notes to save your song!');
      return;
    }
    
    toast.success('Star Song Created! +15 Sparks');
    if (onAwardSparks) {
      await onAwardSparks(15, 'creativity');
    }
    setComposition([]);
    setCurrentStage('menu');
  };
  
  // ============ RENDER STAFF ============
  const renderStaff = (interactive = true, highlightNote = null, showAllNotes = true) => {
    const staffLines = [0, 1, 2, 3, 4]; // 5 lines
    const notePositions = {
      'E4': { line: 0, space: false },
      'F4': { line: 0, space: true },
      'G4': { line: 1, space: false },
      'A4': { line: 1, space: true },
      'B4': { line: 2, space: false },
      'C5': { line: 2, space: true },
      'D4': { line: -1, space: true }, // Below staff
      'C4': { line: -1, space: false }, // Below staff with ledger line
    };
    
    return (
      <div className="relative bg-gradient-to-b from-indigo-950/50 to-purple-950/50 rounded-2xl p-4 sm:p-6 border border-indigo-500/30 overflow-hidden">
        {/* Treble clef glow effect */}
        <div className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-4xl sm:text-6xl text-purple-400/60 font-serif">
          𝄞
        </div>
        
        {/* Staff lines */}
        <div className="relative ml-10 sm:ml-16 h-32 sm:h-40">
          {staffLines.map((_, idx) => (
            <div
              key={idx}
              className="absolute w-full h-0.5 bg-gradient-to-r from-purple-400/40 via-cyan-400/40 to-purple-400/40"
              style={{ top: `${idx * 25}%` }}
            />
          ))}
          
          {/* Notes */}
          {showAllNotes && NOTE_ORDER.map((noteKey, idx) => {
            const note = NOTES[noteKey];
            const isHighlighted = highlightNote === noteKey || highlightedNote === noteKey;
            const isExplored = exploredNotes.includes(noteKey);
            
            // Calculate vertical position (inverted for staff)
            const yPosition = 100 - (note.staffPosition * 12.5);
            const xPosition = 8 + (idx * 11.5);
            
            return (
              <button
                key={noteKey}
                onClick={() => interactive && handleNoteClick(noteKey)}
                disabled={!interactive}
                className={`absolute w-7 h-7 sm:w-8 sm:h-8 rounded-full transition-all duration-300 flex items-center justify-center
                  ${isHighlighted 
                    ? 'bg-gradient-to-br from-cyan-400 to-purple-500 scale-125 shadow-lg shadow-cyan-500/50' 
                    : isExplored
                      ? 'bg-gradient-to-br from-purple-500/80 to-indigo-500/80'
                      : 'bg-slate-700/80 hover:bg-purple-500/60'}
                  ${interactive ? 'cursor-pointer hover:scale-110 active:scale-95' : 'cursor-default'}
                `}
                style={{
                  left: `${xPosition}%`,
                  top: `${yPosition}%`,
                  transform: 'translate(-50%, -50%)',
                }}
                data-testid={`note-${noteKey}`}
              >
                <span className="text-white text-xs font-bold">{note.name}</span>
                {isHighlighted && (
                  <div className="absolute inset-0 rounded-full animate-ping bg-cyan-400/30" />
                )}
              </button>
            );
          })}
        </div>
        
        {/* Ledger line for C4 */}
        <div className="absolute bottom-3 sm:bottom-4 left-14 sm:left-20 w-10 sm:w-12 h-0.5 bg-purple-400/40" />
      </div>
    );
  };
  
  // ============ RENDER MENU ============
  const renderMenu = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500/30 to-cyan-500/30 border border-purple-500/50 mb-4">
          <Music className="w-10 h-10 text-purple-400" />
        </div>
        <h2 className="text-3xl font-philosopher font-bold text-white glow-text">Music Learning Garden</h2>
        <p className="text-slate-400 mt-2">Discover the magic of reading music!</p>
      </div>
      
      {/* Stage Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Stage 1: Staff Explorer */}
        <button
          onClick={() => setCurrentStage('staff-explorer')}
          className="glass-panel p-6 rounded-2xl text-left hover:border-purple-500/50 transition-all group relative overflow-hidden"
          data-testid="music-stage-staff"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <span className="text-2xl">🎼</span>
              </div>
              <div>
                <span className="text-xs text-purple-400 uppercase tracking-wider">Stage 1</span>
                <h3 className="text-xl font-bold text-white">Staff Explorer</h3>
              </div>
            </div>
            <p className="text-slate-400 text-sm">Tap notes on the staff to hear them. Learn where each note lives!</p>
            <div className="mt-4 flex items-center gap-2 text-purple-400 text-sm">
              <Sparkles className="w-4 h-4" />
              <span>+5 sparks</span>
            </div>
          </div>
        </button>
        
        {/* Stage 2: Rhythm Builder */}
        <button
          onClick={startRhythmBuilder}
          className="glass-panel p-6 rounded-2xl text-left hover:border-cyan-500/50 transition-all group relative overflow-hidden"
          data-testid="music-stage-rhythm"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <span className="text-2xl">🥁</span>
              </div>
              <div>
                <span className="text-xs text-cyan-400 uppercase tracking-wider">Stage 2</span>
                <h3 className="text-xl font-bold text-white">Rhythm Builder</h3>
              </div>
            </div>
            <p className="text-slate-400 text-sm">Listen to a pattern, then tap it back! Feel the beat.</p>
            <div className="mt-4 flex items-center gap-2 text-cyan-400 text-sm">
              <Sparkles className="w-4 h-4" />
              <span>+5 sparks</span>
            </div>
          </div>
        </button>
        
        {/* Stage 3: Play Constellation */}
        <button
          onClick={startPlayConstellation}
          className="glass-panel p-6 rounded-2xl text-left hover:border-amber-500/50 transition-all group relative overflow-hidden"
          data-testid="music-stage-play"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <span className="text-2xl">⭐</span>
              </div>
              <div>
                <span className="text-xs text-amber-400 uppercase tracking-wider">Stage 3</span>
                <h3 className="text-xl font-bold text-white">Play Constellation</h3>
              </div>
            </div>
            <p className="text-slate-400 text-sm">Follow the melody by pressing the right notes. Connect the stars!</p>
            <div className="mt-4 flex items-center gap-2 text-amber-400 text-sm">
              <Sparkles className="w-4 h-4" />
              <span>+10 sparks</span>
            </div>
          </div>
        </button>
        
        {/* Stage 4: Compose Star Song */}
        <button
          onClick={() => setCurrentStage('compose-star')}
          className="glass-panel p-6 rounded-2xl text-left hover:border-pink-500/50 transition-all group relative overflow-hidden"
          data-testid="music-stage-compose"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-pink-500/10 rounded-full blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                <span className="text-2xl">✨</span>
              </div>
              <div>
                <span className="text-xs text-pink-400 uppercase tracking-wider">Stage 4</span>
                <h3 className="text-xl font-bold text-white">Compose Star Song</h3>
              </div>
            </div>
            <p className="text-slate-400 text-sm">Create your own magical melody! Be the composer.</p>
            <div className="mt-4 flex items-center gap-2 text-pink-400 text-sm">
              <Sparkles className="w-4 h-4" />
              <span>+15 sparks</span>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
  
  // ============ RENDER STAFF EXPLORER ============
  const renderStaffExplorer = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-philosopher font-bold text-white flex items-center justify-center gap-2">
          <span className="text-3xl">🎼</span> Staff Explorer
        </h2>
        <p className="text-slate-400 mt-2">Tap each note to hear how it sounds!</p>
      </div>
      
      {/* Progress */}
      <div className="flex justify-center gap-2 mb-4">
        {NOTE_ORDER.map((note) => (
          <div
            key={note}
            className={`w-3 h-3 rounded-full transition-all ${
              exploredNotes.includes(note)
                ? 'bg-gradient-to-r from-purple-500 to-cyan-500'
                : 'bg-slate-700'
            }`}
          />
        ))}
      </div>
      <p className="text-center text-sm text-slate-400 mb-6">
        {exploredNotes.length} / {NOTE_ORDER.length} notes explored
      </p>
      
      {/* Staff */}
      {renderStaff(true)}
      
      {/* Note info panel */}
      {highlightedNote && (
        <div className="glass-panel p-4 rounded-xl border border-cyan-500/30 animate-fade-in">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/30 to-purple-500/30 flex items-center justify-center">
              <span className="text-3xl font-bold text-white">{NOTES[highlightedNote].name}</span>
            </div>
            <div>
              <p className="text-white font-semibold">Note {NOTES[highlightedNote].name}{NOTES[highlightedNote].octave}</p>
              <p className="text-slate-400 text-sm">Frequency: {NOTES[highlightedNote].frequency.toFixed(1)} Hz</p>
              <p className="text-cyan-400 text-sm mt-1">
                {highlightedNote === 'C4' && "Middle C - The center of the piano!"}
                {highlightedNote === 'G4' && "G is on the second line from bottom"}
                {highlightedNote === 'A4' && "Concert A - Orchestras tune to this note!"}
                {highlightedNote === 'E4' && "E sits right on the bottom line"}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Complete button */}
      {exploredNotes.length >= NOTE_ORDER.length && (
        <button
          onClick={completeStaffExplorer}
          className="w-full py-4 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          data-testid="complete-staff-explorer"
        >
          <Check className="w-5 h-5" />
          Complete Staff Explorer
        </button>
      )}
    </div>
  );
  
  // ============ RENDER RHYTHM BUILDER ============
  const renderRhythmBuilder = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-philosopher font-bold text-white flex items-center justify-center gap-2">
          <span className="text-2xl sm:text-3xl">🥁</span> Rhythm Builder
        </h2>
        <p className="text-slate-400 mt-2 text-sm sm:text-base">Listen to the pattern, then tap it back!</p>
      </div>
      
      {currentRhythm && (
        <>
          <div className="glass-panel p-4 sm:p-6 rounded-xl border border-cyan-500/30">
            <p className="text-white font-semibold mb-4 text-center text-sm sm:text-base">{currentRhythm.name}</p>
            
            {/* Pattern visualization */}
            <div className="flex justify-center gap-2 sm:gap-3 mb-4 sm:mb-6 flex-wrap">
              {currentRhythm.pattern.map((beat, idx) => (
                <div
                  key={idx}
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center transition-all ${
                    beat === 1
                      ? 'bg-gradient-to-br from-cyan-500 to-purple-500'
                      : 'bg-slate-700/50 border border-slate-600'
                  }`}
                >
                  {beat === 1 ? (
                    <Volume2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  ) : (
                    <span className="text-slate-500">—</span>
                  )}
                </div>
              ))}
            </div>
            
            {/* Listen button */}
            <button
              onClick={playRhythmPattern}
              disabled={rhythmPlaying}
              className="w-full py-2 sm:py-3 rounded-xl bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 font-semibold hover:bg-cyan-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
              data-testid="play-rhythm-pattern"
            >
              {rhythmPlaying ? (
                <>
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                  Playing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 sm:w-5 sm:h-5" />
                  Listen to Pattern
                </>
              )}
            </button>
          </div>
          
          {/* User input area */}
          <div className="glass-panel p-4 sm:p-6 rounded-xl">
            <p className="text-white font-semibold mb-4 text-center text-sm sm:text-base">Your Turn!</p>
            
            {/* User pattern display */}
            <div className="flex justify-center gap-2 sm:gap-3 mb-4 sm:mb-6 flex-wrap">
              {Array.from({ length: currentRhythm.pattern.length }).map((_, idx) => (
                <div
                  key={idx}
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center transition-all ${
                    idx < userRhythm.length
                      ? userRhythm[idx] === 1
                        ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                        : 'bg-slate-700/50 border border-amber-500/30'
                      : 'bg-slate-800/50 border border-slate-700'
                  }`}
                >
                  {idx < userRhythm.length && (
                    userRhythm[idx] === 1 ? (
                      <Volume2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    ) : (
                      <span className="text-slate-500">—</span>
                    )
                  )}
                </div>
              ))}
            </div>
            
            {/* Input buttons */}
            <div className="flex justify-center gap-2 sm:gap-4 mb-4">
              <button
                onClick={handleRhythmTap}
                disabled={userRhythm.length >= currentRhythm.pattern.length}
                className="px-4 sm:px-8 py-3 sm:py-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-base sm:text-lg hover:opacity-90 transition-opacity disabled:opacity-50 active:scale-95"
                data-testid="rhythm-tap"
              >
                TAP! 🥁
              </button>
              <button
                onClick={handleRhythmRest}
                disabled={userRhythm.length >= currentRhythm.pattern.length}
                className="px-4 sm:px-8 py-3 sm:py-4 rounded-xl bg-slate-700 border border-slate-600 text-slate-300 font-bold text-base sm:text-lg hover:bg-slate-600 transition-colors disabled:opacity-50 active:scale-95"
                data-testid="rhythm-rest"
              >
                REST —
              </button>
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={resetRhythm}
                className="flex-1 py-3 rounded-xl bg-slate-700/50 text-slate-300 font-semibold hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
              <button
                onClick={checkRhythm}
                disabled={userRhythm.length < currentRhythm.pattern.length}
                className="flex-1 py-3 rounded-xl bg-green-500/20 border border-green-500/50 text-green-400 font-semibold hover:bg-green-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="check-rhythm"
              >
                <Check className="w-5 h-5" />
                Check
              </button>
            </div>
          </div>
          
          {/* Result */}
          {rhythmScore !== null && (
            <div className={`glass-panel p-6 rounded-xl text-center ${
              rhythmScore ? 'border-green-500/50' : 'border-red-500/50'
            }`}>
              {rhythmScore ? (
                <>
                  <div className="text-5xl mb-3">🎉</div>
                  <p className="text-green-400 font-bold text-xl">Perfect Rhythm!</p>
                  <p className="text-slate-400 mt-2">You matched the pattern exactly!</p>
                </>
              ) : (
                <>
                  <div className="text-5xl mb-3">🔄</div>
                  <p className="text-amber-400 font-bold text-xl">Almost there!</p>
                  <p className="text-slate-400 mt-2">Try listening again and matching the beats.</p>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
  
  // ============ RENDER PLAY CONSTELLATION ============
  const renderPlayConstellation = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-philosopher font-bold text-white flex items-center justify-center gap-2">
          <span className="text-3xl">⭐</span> Play Constellation
        </h2>
        <p className="text-slate-400 mt-2">Follow the melody by pressing the matching notes!</p>
      </div>
      
      {currentMelody && (
        <>
          {/* Melody info */}
          <div className="glass-panel p-6 rounded-xl border border-amber-500/30 text-center">
            <p className="text-amber-400 font-semibold text-lg mb-2">{currentMelody.name}</p>
            
            {/* Progress stars */}
            <div className="flex justify-center gap-2 mb-4">
              {currentMelody.notes.map((_, idx) => (
                <Star
                  key={idx}
                  className={`w-6 h-6 transition-all ${
                    idx < melodyIndex
                      ? 'text-amber-400 fill-amber-400'
                      : idx === melodyIndex
                        ? 'text-amber-400 animate-pulse'
                        : 'text-slate-600'
                  }`}
                />
              ))}
            </div>
            
            <p className="text-slate-400 text-sm">
              {melodyComplete 
                ? 'Melody complete!' 
                : `Note ${melodyIndex + 1} of ${currentMelody.notes.length}`}
            </p>
            
            {/* Demo button */}
            <button
              onClick={playMelodyDemo}
              disabled={melodyPlaying}
              className="mt-4 px-6 py-2 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-400 font-semibold hover:bg-amber-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
              data-testid="play-melody-demo"
            >
              {melodyPlaying ? (
                <>
                  <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                  Playing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Hear Melody
                </>
              )}
            </button>
          </div>
          
          {/* Interactive keyboard */}
          <div className="glass-panel p-4 sm:p-6 rounded-xl">
            <p className="text-white font-semibold mb-4 text-center text-sm sm:text-base">
              {melodyComplete ? 'Amazing work!' : `Press: ${NOTES[currentMelody.notes[melodyIndex]]?.name || '?'}`}
            </p>
            
            {/* Piano keys - responsive grid */}
            <div className="flex justify-center gap-1 sm:gap-2 overflow-x-auto px-2">
              {NOTE_ORDER.map((noteKey) => {
                const note = NOTES[noteKey];
                const isTarget = !melodyComplete && currentMelody.notes[melodyIndex] === noteKey;
                const wasPressed = userMelodyInput.find(u => u.note === noteKey);
                
                return (
                  <button
                    key={noteKey}
                    onClick={() => handleMelodyNotePress(noteKey)}
                    disabled={melodyComplete}
                    className={`w-10 h-16 sm:w-14 sm:h-24 rounded-lg flex flex-col items-center justify-end pb-1 sm:pb-2 transition-all transform hover:scale-105 active:scale-95 flex-shrink-0 ${
                      isTarget
                        ? 'bg-gradient-to-b from-amber-400 to-amber-600 shadow-lg shadow-amber-500/50 animate-pulse'
                        : 'bg-gradient-to-b from-slate-200 to-slate-400 hover:from-purple-300 hover:to-purple-500'
                    } disabled:opacity-50`}
                    data-testid={`piano-${noteKey}`}
                  >
                    <span className={`font-bold text-sm sm:text-lg ${isTarget ? 'text-white' : 'text-slate-700'}`}>
                      {note.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Success message */}
          {melodyComplete && (
            <div className="glass-panel p-6 rounded-xl border border-green-500/50 text-center">
              <div className="text-5xl mb-3">🌟</div>
              <p className="text-green-400 font-bold text-xl">Constellation Complete!</p>
              <p className="text-slate-400 mt-2">You played "{currentMelody.name}" perfectly!</p>
              <button
                onClick={() => setCurrentStage('menu')}
                className="mt-4 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold hover:opacity-90"
              >
                Continue Learning
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
  
  // ============ RENDER COMPOSE STAR SONG ============
  const renderComposeStarSong = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-philosopher font-bold text-white flex items-center justify-center gap-2">
          <span className="text-3xl">✨</span> Compose Star Song
        </h2>
        <p className="text-slate-400 mt-2">Create your own magical melody!</p>
      </div>
      
      {/* Composition display */}
      <div className="glass-panel p-6 rounded-xl border border-pink-500/30">
        <div className="flex items-center justify-between mb-4">
          <p className="text-pink-400 font-semibold">Your Melody ({composition.length}/16 notes)</p>
          <div className="flex gap-2">
            <button
              onClick={removeLastNote}
              disabled={composition.length === 0}
              className="p-2 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={clearComposition}
              disabled={composition.length === 0}
              className="p-2 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Notes display */}
        <div className="flex flex-wrap gap-2 min-h-16 p-4 bg-slate-900/50 rounded-xl border border-slate-700">
          {composition.length === 0 ? (
            <p className="text-slate-500 text-sm">Tap notes below to start composing...</p>
          ) : (
            composition.map((noteKey, idx) => (
              <div
                key={idx}
                className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center animate-fade-in"
              >
                <span className="text-white font-bold text-sm">{NOTES[noteKey]?.name}</span>
              </div>
            ))
          )}
        </div>
        
        {/* Play composition */}
        <button
          onClick={playComposition}
          disabled={composition.length === 0 || isPlayingComposition}
          className="w-full mt-4 py-3 rounded-xl bg-pink-500/20 border border-pink-500/50 text-pink-400 font-semibold hover:bg-pink-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="play-composition"
        >
          {isPlayingComposition ? (
            <>
              <div className="w-5 h-5 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
              Playing...
            </>
          ) : (
            <>
              <Play className="w-5 h-5" />
              Play Your Song
            </>
          )}
        </button>
      </div>
      
      {/* Piano keyboard */}
      <div className="glass-panel p-4 sm:p-6 rounded-xl">
        <p className="text-white font-semibold mb-4 text-center text-sm sm:text-base">Tap to add notes</p>
        
        <div className="flex justify-center gap-1 sm:gap-2 overflow-x-auto px-2">
          {NOTE_ORDER.map((noteKey) => {
            const note = NOTES[noteKey];
            
            return (
              <button
                key={noteKey}
                onClick={() => addNoteToComposition(noteKey)}
                disabled={composition.length >= 16}
                className="w-10 h-16 sm:w-14 sm:h-24 rounded-lg bg-gradient-to-b from-slate-200 to-slate-400 hover:from-pink-300 hover:to-pink-500 flex flex-col items-center justify-end pb-1 sm:pb-2 transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 flex-shrink-0"
                data-testid={`compose-${noteKey}`}
              >
                <span className="font-bold text-sm sm:text-lg text-slate-700">{note.name}</span>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Save button */}
      <button
        onClick={saveComposition}
        disabled={composition.length < 4}
        className="w-full py-4 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold text-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="save-composition"
      >
        <Sparkles className="w-5 h-5" />
        Save Star Song (+15 Sparks)
      </button>
    </div>
  );
  
  // ============ MAIN RENDER ============
  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => currentStage === 'menu' ? onBack() : setCurrentStage('menu')}
        className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        data-testid="music-garden-back"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>{currentStage === 'menu' ? 'Back to Courtyard' : 'Back to Menu'}</span>
      </button>
      
      {/* Stage content */}
      {currentStage === 'menu' && renderMenu()}
      {currentStage === 'staff-explorer' && renderStaffExplorer()}
      {currentStage === 'rhythm-builder' && renderRhythmBuilder()}
      {currentStage === 'play-constellation' && renderPlayConstellation()}
      {currentStage === 'compose-star' && renderComposeStarSong()}
    </div>
  );
};

export default MusicLearningGarden;
